import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { EMBROIDERY_TYPES } from "../../constants/imageGeneratorDictionaries";
import {
  collectionBuildBorderClassName,
  exclusivePillClassName,
  textareaClassName,
} from "./collectionBuildConstants";

const isListedEmbroidery = (value: string) =>
  (EMBROIDERY_TYPES as readonly string[]).includes(value);

type Props = {
  value: string;
  onChange: (value: string) => void;
};

const CollectionBuildEmbroideryType: React.FC<Props> = ({
  value,
  onChange,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const isCustom = Boolean(value) && !isListedEmbroidery(value);
  const listedValue = isCustom ? "" : value;

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    setDraft(isCustom ? value : "");
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setModalOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalOpen, isCustom, value]);

  const submitCustom = () => {
    const next = draft.trim();
    if (!next) {
      return;
    }
    onChange(next);
    setModalOpen(false);
  };

  return (
    <div>
      <div className="flex min-w-0 max-w-full flex-wrap gap-4">
        {EMBROIDERY_TYPES.filter(emb => emb !== "Describe your own").map(
          emb => {
            const isActive = listedValue === emb;
            return (
              <button
                key={emb}
                type="button"
                onClick={() => onChange(isActive ? "" : emb)}
                className={exclusivePillClassName(isActive)}
              >
                {emb}
              </button>
            );
          }
        )}
        <button
          type="button"
          title="Describe your own"
          onClick={() => {
            if (isCustom) {
              onChange("");
              return;
            }
            setModalOpen(true);
          }}
          className={exclusivePillClassName(isCustom)}
        >
          Describe your own
        </button>
      </div>

      {modalOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[220] flex items-center justify-center font-avenir"
              role="dialog"
              aria-modal="true"
              aria-label="Describe your own embroidery"
            >
              <div
                className="fixed inset-0 bg-[#1A1A1A]/85"
                onClick={() => setModalOpen(false)}
              />
              <div
                className="relative z-10 w-full max-w-[480px] rounded-2xl bg-white p-5 shadow-[0_16px_48px_rgba(0,0,0,0.28)]"
                onClick={event => event.stopPropagation()}
              >
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-avenir text-base font-semibold text-primary">
                    Describe your own
                  </h2>
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-secondary hover:bg-lightGray focus:outline-none"
                    aria-label="Close"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6 6l12 12M18 6 6 18"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
                <label className="mb-4 block">
                  <span className="mb-1.5 block text-sm text-secondary">
                    Embroidery pattern
                  </span>
                  <textarea
                    value={draft}
                    onChange={event => setDraft(event.target.value)}
                    placeholder="Describe your own embroidery pattern"
                    maxLength={240}
                    rows={4}
                    className={textareaClassName}
                    autoFocus
                  />
                </label>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className={`h-10 rounded-full border ${collectionBuildBorderClassName} bg-white px-5 font-avenir text-sm text-secondary hover:bg-lightGray focus:outline-none`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!draft.trim()}
                    onClick={submitCustom}
                    className="h-10 rounded-full bg-black px-6 font-avenir text-sm text-white hover:bg-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Use this
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default CollectionBuildEmbroideryType;
